// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useNavigate } from "@tanstack/react-router";
import type { PaginationState, SortingState } from "@tanstack/react-table";
import { BellMinus, BellPlus, BellRing, MoreHorizontal } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  type UserAlertRow,
  type UserAlertSource,
  useSetUserAlertSubscriptionMutation,
  useUserAlertsQuery,
} from "@/api/hooks/use-users";
import { DataTable, type DataTableColumnDef } from "@/components/data-table";
import { DateValue } from "@/components/shared/datetime-value";
import { FilterSelect } from "@/components/shared/filter-select";
import { FilterToggle } from "@/components/shared/filter-toggle";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { SubscribeUserAlertDialog } from "@/features/iam/subscribe-user-alert-dialog";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { decodeFilters, encodeFilters } from "@/lib/filter-params";
import { getDefaultPageSize } from "@/lib/preferences";

type UserAlertsSortBy = "alert" | "receives" | "subscribedAt";

// Round-tripped through the shared `?f=` param, same as the Activity tab on this page. Only
// non-default values are encoded, so the plain tab link stays `?t=alerts`.
const FILTER_DEFAULTS = {
  search: "",
  onlyReceived: true,
  page: 0,
  get pageSize() {
    return getDefaultPageSize();
  },
  source: "",
  sortId: "alert",
  sortDesc: false,
};

/**
 * Where the subscription comes from: their own row, a row on a dotted ancestor that also catches
 * this alert, or a row of their own that has been switched off.
 */
function SourceCell({ row }: { row: UserAlertRow }) {
  const { t } = useTranslation("back-office");

  if (row.source === "direct") {
    return <Badge variant="secondary">{t("users.alerts.source.direct")}</Badge>;
  }
  if (row.source === "inherited") {
    return (
      <span className="flex flex-wrap items-center gap-1.5">
        <Badge variant="outline" className="font-normal">
          {t("users.alerts.source.inherited")}
        </Badge>
        <span className="font-mono text-xs text-muted-foreground">{row.sourceAlert}</span>
      </span>
    );
  }
  // No delivery, but a row of their own explains why: somebody unsubscribed them
  if (row.subscriptionActive === false) {
    return (
      <Badge variant="outline" className="font-normal text-muted-foreground">
        {t("users.alerts.source.disabled")}
      </Badge>
    );
  }
  return <span className="text-muted-foreground">—</span>;
}

/**
 * Subscribe / unsubscribe for one alert. Unsubscribe is offered only where there is a row of the
 * user's own to switch off: an inherited alert is delivered by a subscription on an ancestor, and
 * dropping that would unsubscribe them from every alert under it.
 */
function AlertActionsMenu({
  row,
  onSubscribe,
  onUnsubscribe,
}: {
  row: UserAlertRow;
  onSubscribe: () => void;
  onUnsubscribe: () => void;
}) {
  const { t } = useTranslation("back-office");
  const isDirect = row.source === "direct";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={t("users.alerts.actions.menu")}>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={-32} className="dropdown-overlap">
        {isDirect ? (
          <DropdownMenuItem onSelect={onUnsubscribe} className="text-destructive focus:text-destructive">
            <BellMinus className="h-4 w-4 text-destructive" />
            {t("users.alerts.actions.unsubscribe")}
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem onSelect={onSubscribe}>
            <BellPlus className="h-4 w-4" />
            {t(
              row.source === "inherited" ? "users.alerts.actions.subscribeDirectly" : "users.alerts.actions.subscribe",
            )}
          </DropdownMenuItem>
        )}
        {row.source === "inherited" && (
          // Kept visible-but-disabled (with the reason) rather than hidden, so the restriction is
          // discoverable — the ancestor's own row is where to go.
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuItem
                onSelect={(e) => e.preventDefault()}
                aria-disabled
                className="text-destructive opacity-50 cursor-not-allowed focus:text-destructive"
              >
                <BellMinus className="h-4 w-4 text-destructive" />
                {t("users.alerts.actions.unsubscribe")}
              </DropdownMenuItem>
            </TooltipTrigger>
            <TooltipContent>
              {t("users.alerts.actions.inheritedCannotUnsubscribe", { alert: row.sourceAlert })}
            </TooltipContent>
          </Tooltip>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface UserAlertsTabProps {
  publicId: string;
  /** Encoded filter state from the route's `?f=` param. */
  initialFilters?: string;
}

/**
 * Every alert `messaging_alert_receivers` knows about, marked up with whether this user receives
 * it — the backoffice answer to "why did (or didn't) this user get alert X?". Read-only: alert
 * subscriptions are still managed in the database.
 */
export function UserAlertsTab({ publicId, initialFilters }: UserAlertsTabProps) {
  const { t } = useTranslation("back-office");
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(PAGE_PERMISSIONS.iamUsersAlertsEdit);
  const initial = useMemo(() => decodeFilters(initialFilters, FILTER_DEFAULTS), [initialFilters]);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initial.page,
    pageSize: initial.pageSize,
  });
  const [sorting, setSorting] = useState<SortingState>([{ id: initial.sortId, desc: initial.sortDesc }]);
  const [search, setSearch] = useState(initial.search);
  // The tab opens on what the user actually gets; the toggle widens it to the whole catalogue
  const [onlyReceived, setOnlyReceived] = useState(initial.onlyReceived);
  const [sourceFilter, setSourceFilter] = useState(initial.source);
  const [subscribeOpen, setSubscribeOpen] = useState(false);

  const setSubscription = useSetUserAlertSubscriptionMutation(publicId);

  const sortBy = (sorting[0]?.id ?? "alert") as UserAlertsSortBy;
  const orderBy = sorting[0]?.desc ? ("desc" as const) : ("asc" as const);

  // The single description of "what is filtered right now", shared by the URL sync
  const currentFilters = useMemo(
    () => ({
      search,
      onlyReceived,
      page: pagination.pageIndex,
      pageSize: pagination.pageSize,
      source: sourceFilter,
      sortId: sorting[0]?.id ?? "alert",
      sortDesc: sorting[0]?.desc ?? false,
    }),
    [search, onlyReceived, sourceFilter, pagination.pageIndex, pagination.pageSize, sorting],
  );

  const syncUrl = useCallback(
    function syncUrl() {
      const encoded = encodeFilters(currentFilters, FILTER_DEFAULTS);
      navigate({
        to: "/iam/users/$publicId",
        params: { publicId },
        search: { t: "alerts", ...(encoded ? { f: encoded } : {}) },
        replace: true,
      });
    },
    [currentFilters, navigate, publicId],
  );

  useEffect(
    function synchronizeFiltersToUrl() {
      syncUrl();
    },
    [syncUrl],
  );

  const { data, isLoading, refetch, isFetching } = useUserAlertsQuery({
    publicId,
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search,
    sortBy,
    orderBy,
    onlyReceived: onlyReceived || undefined,
    source: sourceFilter as UserAlertSource | "",
  });

  const changeSubscription = useCallback(
    async function changeSubscription(alert: string, isActive: boolean) {
      try {
        await setSubscription.mutateAsync({ alert, isActive });
        toast.success(
          t(isActive ? "users.alerts.notifications.subscribed" : "users.alerts.notifications.unsubscribed", { alert }),
        );
      } catch {
        // Error toast handled by API middleware
      }
    },
    [setSubscription, t],
  );

  const columns = useMemo<DataTableColumnDef<UserAlertRow>[]>(() => {
    const base: DataTableColumnDef<UserAlertRow>[] = [
      {
        accessorKey: "alert",
        header: t("users.alerts.columns.alert"),
        meta: { columnLabel: t("users.alerts.columns.alert"), className: "pl-4" },
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.alert}</span>,
      },
      {
        accessorKey: "receives",
        header: t("users.alerts.columns.receives"),
        size: 100,
        meta: { columnLabel: t("users.alerts.columns.receives"), forceWidth: true },
        // Read-only switch — the house widget for a boolean in a table cell
        cell: ({ row }) => {
          const label = t(row.original.receives ? "users.alerts.receives.yes" : "users.alerts.receives.no");
          return <Switch checked={row.original.receives} aria-label={label} className="pointer-events-none" />;
        },
      },
      {
        accessorKey: "source",
        header: t("users.alerts.columns.source"),
        enableSorting: false,
        meta: { columnLabel: t("users.alerts.columns.source") },
        cell: ({ row }) => <SourceCell row={row.original} />,
      },
      {
        accessorKey: "subscribedAt",
        header: t("users.alerts.columns.subscribedAt"),
        size: 140,
        meta: { columnLabel: t("users.alerts.columns.subscribedAt"), forceWidth: true },
        cell: ({ row }) =>
          row.original.subscribedAt ? (
            <DateValue value={row.original.subscribedAt} />
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
    ];
    if (canEdit) {
      base.push({
        id: "actions",
        size: 50,
        enableSorting: false,
        enableHiding: false,
        meta: { forceWidth: true, sticky: "right" },
        cell: ({ row }) => (
          <AlertActionsMenu
            row={row.original}
            onSubscribe={() => changeSubscription(row.original.alert, true)}
            onUnsubscribe={() => changeSubscription(row.original.alert, false)}
          />
        ),
      });
    }
    return base;
  }, [t, canEdit, changeSubscription]);

  return (
    <div className="p-6">
      <DataTable
        storageKey="user-alerts"
        columns={columns}
        data={data?.alerts ?? []}
        totalCount={data?.count ?? 0}
        isLoading={isLoading}
        pagination={pagination}
        onPaginationChange={setPagination}
        sorting={sorting}
        onSortingChange={setSorting}
        search={search}
        onSearchChange={(next) => {
          setSearch(next);
          setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        }}
        searchDebounceMs={300}
        searchPlaceholder={t("users.alerts.searchPlaceholder")}
        filters={
          <>
            <FilterToggle
              active={onlyReceived}
              onChange={(active) => {
                setOnlyReceived(active);
                setPagination((prev) => ({ ...prev, pageIndex: 0 }));
              }}
              icon={BellRing}
              label={t("users.alerts.onlyReceived")}
            />
            <FilterSelect
              label={t("users.alerts.filter.source")}
              value={sourceFilter}
              onChange={(next) => {
                setSourceFilter(next);
                setPagination((prev) => ({ ...prev, pageIndex: 0 }));
              }}
              options={[
                { label: t("users.alerts.source.direct"), value: "direct" },
                { label: t("users.alerts.source.inherited"), value: "inherited" },
              ]}
              searchPlaceholder={t("stores.operations.searchOption")}
              emptyText={t("stores.operations.noOption")}
            />
          </>
        }
        toolbar={
          canEdit ? (
            <Button size="sm" className="h-9" onClick={() => setSubscribeOpen(true)}>
              <BellPlus className="h-4 w-4" />
              {t("users.alerts.dialog.subscribeAction")}
            </Button>
          ) : undefined
        }
        onRefresh={refetch}
        isRefreshing={isFetching}
      />
      <SubscribeUserAlertDialog publicId={publicId} open={subscribeOpen} onOpenChange={setSubscribeOpen} />
    </div>
  );
}
